// © Microsoft Corporation. All rights reserved.

import { ConfigOrigins } from "@msnews/core-contracts";
import { Manifest } from "@msnews/config-data-types";
import { fetchAsync } from "./BlobFetcher";
import { isEmpty } from "lodash-es";
import { useLocalConfigs } from "../../../utilities";

/**
 * Manifest Service class
 * This is for fetching and parsing the Manifest into an easily consumable form.
 */
export class ClientManifestService {
    /** Manifest map to quickly return already fetched manifests. */
    private manifestsMap: Map<string, Manifest> = new Map<string, Manifest>();

    /** Application Type */
    private appType: string;

    /** Indicates if configs should be served from local repo */
    private configOrigins: ConfigOrigins;

    /**
     * Initialize client manifest service based on app type and if configs should be served from local repo
     * @param appType
     * @param ConfigOrigins Indicates if configs should be served from local repo
     */
    constructor(appType: string, configOrigins: ConfigOrigins) {
        this.appType = appType;
        this.configOrigins= configOrigins;
    }

    /**
     * This function fetches and parses manifest file from azure blob storage.
     * @param {string} manifestType config type, loc-manifests or feature-manifests
     * @param {string} version build version
     */
    public async fetchAndParseManifest(manifestType: string, version: string): Promise<Manifest> {
        if (useLocalConfigs(manifestType, this.configOrigins)) {
            return undefined;
        }

        const relativeUrl = `${manifestType}/${this.appType}/${version}.json`;

        // We don't need a promise map check because it's done in BlobFetcher.
        if (!this.manifestsMap.has(manifestType)) {
            try {
                const manifestBlobResult = await fetchAsync(relativeUrl);
                this.manifestsMap.set(manifestType, manifestBlobResult);
                if (isEmpty(manifestBlobResult)) {
                    throw new Error(`Manifest response is ok but empty or undefined, relative url: ${relativeUrl}`);
                }
            } catch (error) {
                // Update the value to null no matter what the error is.
                this.manifestsMap.set(manifestType, null);
                throw new Error(`ClientManifestService.ts: ${error}`);
            }
        }

        // Return for easy testing
        // todo: would this cause perf problem since feature manifest is huge?
        return this.manifestsMap.get(manifestType);
    }

    /**
     * Gets the hashed file name from the manifest by creating config index map key.
     * @param {string} configReference config reference: combination of experienceType, instance source and shared namespace.
     * @param {string} manifestType manifest type, feature-manifests vs localization-manifests
     */
    public getConfigIndexHashedFileName(configReference: string, manifestType: string): string {
        if (useLocalConfigs(manifestType, this.configOrigins)) {
            return undefined;
        }

        const configIndexKey = `${configReference}/index.json`;
        return this.getFromManifest(configIndexKey, manifestType);
    }

    /**
     * Gets the hashed file name from the manifest by creating config map key.
     * @param {string} configReference config reference made from entity information.
     * @param {string} configFileName config src file name.
     * @param {string} manifestType manifest type, feature-manifests vs localization-manifests
     */
    public getConfigHashedFileName(configReference: string, configFileName: string, manifestType: string): string {
        if (useLocalConfigs(manifestType, this.configOrigins)) {
            return undefined;
        }

        const configKey = `${configReference}/${configFileName}`;
        return this.getFromManifest(configKey, manifestType);
    }

    /**
     * Gets the value according to cache key from the local map cache.
     * @param {string} cacheKey cache key to read from the map, either for index or config.
     * @param {string} manifestType manifest type, feature-manifests vs localization-manifests
     */
    private getFromManifest(cacheKey: string, manifestType: string): string {
        if (!this.manifestsMap || !this.manifestsMap.get(manifestType)) {
            return undefined;
        }

        const manifest = this.manifestsMap.get(manifestType);
        if (manifest && manifest.fileHashes) {
            return manifest.fileHashes[cacheKey.toLowerCase()];
        }

        throw new Error(`ClientManifestService.ts: Manifest is defined but does not have fileHashes: ${cacheKey}: cacheKey snippet of manifest: ${JSON.stringify(manifest).substr(0, 100)}`);
    }
}